import { useCallback, useEffect, useMemo, useState } from "react";
import * as services from "../service/MyTeamLeaveCalendarService";
import { useDispatch, useSelector } from "react-redux";
import { setCalendarEndDate, setCalendarStartDate, setEmployeeRequestCalendar, setIsLeaveHrView, setIsLoading } from "../slices/myteamSlice";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { Badge, Divider, Flex, Space } from "antd";
import StatusTag from "components/elements/TagCustom/StatusTag";

function useMyTeamLeaveCalendar() {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { currentCompany } = useSelector((state) => state.company);
    const { employeeRequestCalendar, calendarStartDate, calendarEndDate, isLeaveHrView } = useSelector((state) => state.myteam);

    const [month, setMonth] = useState(dayjs().month() + 1);
    const [startDate, setStartDate] = useState(calendarStartDate || dayjs().startOf('month').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(calendarEndDate || dayjs().endOf('month').format("YYYY-MM-DD"));
    const [popoverVisible, setPopoverVisible] = useState({});
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedEmployeeList, setSelectedEmployeeList] = useState([]);

    const getCalendarRange = (date) => {
        const firstDayOfMonth = dayjs(date).startOf('month');
        const lastDayOfMonth = dayjs(date).endOf('month');

        const calendarStart = firstDayOfMonth.day(0);

        const calendarEnd = lastDayOfMonth.day(6);

        return {
            start: calendarStart.format("YYYY-MM-DD"),
            end: calendarEnd.format("YYYY-MM-DD")
        };
    };

    useEffect(() => {
        dispatch(setCalendarStartDate());
        dispatch(setCalendarEndDate());
    }, [dispatch]);

    useMemo(() => {
        if (calendarStartDate && calendarEndDate) {
            setStartDate(calendarStartDate);
            setEndDate(calendarEndDate);
        } else {
            const range = getCalendarRange(dayjs());
            setStartDate(range.start);
            setEndDate(range.end);
        }
    }, [calendarStartDate, calendarEndDate]);

    const shiftColor = useMemo(() => [
        {
            name: "คำขอลางาน",
            nameEn: "Leave request",
            color: "#FF9F2D",
            bgColor: "#FFF5E9"
        },
        {
            name: "คำขอปฏิบัติงานนอกสถานที่",
            nameEn: "Off-site work request",
            color: "#0085FF",
            bgColor: "#E6F3FF"
        }
    ], []);

    const getEmployeeRequestForCalendar = useCallback(async (startDate, endDate, month) => {
        try {
            dispatch(setIsLoading(true))
            const requestParams = {
                month: month,
                startDate: startDate,
                endDate: endDate,
            };

            if (isLeaveHrView) {
                requestParams.roleHr = "Y";
            }

            const response = await services.getEmployeeRequestCalendar(requestParams);

            dispatch(setEmployeeRequestCalendar(response?.data?.content))
        } catch (error) {
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [dispatch, startDate, endDate, isLeaveHrView])

    const handleHrViewChange = useCallback((e) => {
        dispatch(setIsLeaveHrView(e.target.checked));
    }, [dispatch]);

    const onChange = useCallback((values) => {
        const range = getCalendarRange(values);
        const monthFromOnChange = values?.month() + 1;

        setMonth(monthFromOnChange);

        if (range.start !== startDate) {
            setStartDate(range.start);
            setEndDate(range.end);

            dispatch(setCalendarStartDate(range.start));
            dispatch(setCalendarEndDate(range.end));

            getEmployeeRequestForCalendar(range.start, range.end, monthFromOnChange);
        }
    }, [startDate, dispatch, getEmployeeRequestForCalendar]);

    useEffect(() => {
        if (currentCompany) {
            getEmployeeRequestForCalendar(startDate, endDate, month)
        }
    }, [currentCompany, isLeaveHrView])

    const processedEvents = useMemo(() => {
        const events = [];
        const MAX_VISIBLE_EVENTS = 3;

        employeeRequestCalendar?.forEach(dayData => {
            if (dayData?.data && dayData?.data?.length > 0) {
                const totalEmployees = dayData?.data?.length;
                const formattedDate = dayjs(dayData?.date)?.format('YYYY-MM-DD');

                dayData.data?.slice(0, MAX_VISIBLE_EVENTS).forEach((employee, empIndex) => {
                    const hasLeave = employee?.txnLeaveRequest && employee?.txnLeaveRequest.length > 0;
                    const hasOffsite = employee?.txnOffsiteWorkRequest && employee?.txnOffsiteWorkRequest.length > 0;
                    const uniqueId = `${formattedDate}-${employee?.id}-${empIndex}`;

                    events.push({
                        id: uniqueId,
                        title: i18n.language === 'en' && employee?.fullnameEn !== "null null"
                            ? employee?.fullnameEn
                            : employee?.fullname,
                        start: new Date(dayData?.date),
                        end: new Date(dayData?.date),
                        allDay: true,
                        resource: {
                            employee: employee,
                            date: dayData?.date,
                            type: hasLeave ? 'leave' : 'offsite',
                            bgColor: hasLeave ? shiftColor[0]?.bgColor : shiftColor[1]?.bgColor,
                            textColor: hasLeave ? shiftColor[0]?.color : shiftColor[1]?.color,
                        }
                    });
                });

                if (totalEmployees > MAX_VISIBLE_EVENTS) {
                    const remainingCount = totalEmployees - MAX_VISIBLE_EVENTS;

                    events.push({
                        id: `seemore-${dayData?.date}`,
                        title: `+${remainingCount}  ${t("more")}`,
                        start: new Date(dayData?.date),
                        end: new Date(dayData?.date),
                        allDay: true,
                        resource: {
                            date: dayData?.date,
                            type: 'seeMore',
                            remainingCount: remainingCount,
                            allEmployees: dayData?.data,
                            bgColor: 'transparent',
                            textColor: '#969696',
                        }
                    });
                }
            }
        });

        return events;
    }, [employeeRequestCalendar, i18n.language, shiftColor]);

    const eventPropGetter = useCallback((event) => {
        if (event?.resource?.type === "leave") {
            return {
                style: {
                    backgroundColor: "#FFF5E9",
                    color: "#FF9F2D",
                }
            };
        }

        if (event?.resource?.type === "offsite") {
            return {
                style: {
                    backgroundColor: "#E6F3FF",
                    color: "#0085FF",
                }
            };
        }

        if (event?.resource?.type === "seeMore") {
            return {
                style: {
                    backgroundColor: "transparent",
                    color: "#969696"
                }
            }
        }
        return null
    }, [])

    const getPopoverContent = useCallback((event) => {
        const employee = event?.resource?.employee;
        const allRequests = [];

        if (employee?.txnLeaveRequest && employee?.txnLeaveRequest?.length > 0) {
            employee?.txnLeaveRequest?.forEach(leave => {
                allRequests.push({
                    type: 'leave',
                    doc: leave,
                    badgeText: i18n.language === 'en' ? leave?.leaveType?.leaveTypeNameEn : leave?.leaveType?.leaveTypeName,
                    badgeColor: "#FF9F2D",
                    backgroundColor: "#FFF5E9"
                });
            });
        }

        if (employee?.txnOffsiteWorkRequest && employee?.txnOffsiteWorkRequest?.length > 0) {
            employee?.txnOffsiteWorkRequest?.forEach(offsite => {
                allRequests.push({
                    type: 'offsite',
                    doc: offsite,
                    badgeText: i18n.language === 'en' ? offsite?.workTypeId?.leaveTypeNameEn : offsite?.workTypeId?.leaveTypeName,
                    badgeColor: "#0085FF",
                    backgroundColor: "#E6F3FF"
                });
            });
        }

        const eventDate = event?.start && dayjs(event?.start).isValid()
            ? (i18n.language === "en"
                ? dayjs(event?.start)?.format("DD/MM/YYYY")
                : dayjs(event?.start)?.format("DD/MM/BBBB"))
            : '-';

        const calculateDuration = (doc) => {
            if (doc?.durationType === "ALLDAY") {
                return `${doc?.totalDuration} ${t("unit.day")}`;
            } else if (doc?.durationType === "HOUR") {
                const totalMinutes = doc?.totalDuration || 0;
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;

                if (hours > 0 && minutes > 0) {
                    return `${hours} ${t("unit.hours")} ${minutes} ${t("unit.minute")}`;
                } else if (hours > 0) {
                    return `${hours} ${t("unit.hour")}`;
                } else if (minutes > 0) {
                    return `${minutes} ${t("unit.minute")}`;
                }
            }
            return '-';
        };

        return (
            <div style={{ maxHeight: '170px', overflowY: 'auto', width: '100%', scrollbarWidth: "thin" }}>
                <Space direction="vertical" style={{ width: "100%" }} size={16}>
                    <span style={{ color: "#344054", fontSize: "15px", fontWeight: 500 }}>
                        {event?.title || '-'}
                    </span>

                    {allRequests.map((request, index) => (
                        <div key={index}>
                            <Space direction="vertical" style={{ width: "100%" }} size={8}>
                                <Badge
                                    color={request?.badgeColor}
                                    text={request?.badgeText}
                                    style={{
                                        backgroundColor: request?.backgroundColor,
                                        borderRadius: "5px",
                                        padding: "2px 8px",
                                        color: request?.badgeColor,
                                        width: "100%"
                                    }}
                                />
                                <Flex justify="space-between">
                                    <span style={{ color: "#667085", borderLeft: "2px solid #757575", paddingLeft: "8px" }}>
                                        {eventDate}
                                    </span>
                                    <span>{calculateDuration(request?.doc)}</span>
                                </Flex>

                                <Flex justify="space-between" align="end">
                                    <Flex vertical>
                                        <span style={{ fontSize: "12px", color: "#A0AEC0" }}>
                                            {t("expense.expense-detail.request-id")}
                                        </span>
                                        <span>{request.doc?.sequence || '-'}</span>
                                    </Flex>

                                    <div style={{ height: "max-content" }}>
                                        <StatusTag value={request?.doc?.approveStatus || '-'} />
                                    </div>
                                </Flex>
                            </Space>

                            {index < allRequests?.length - 1 && (
                                <Divider style={{ border: "0.5px solid #ddd", margin: "12px 0px" }} />
                            )}
                        </div>
                    ))}
                </Space>
            </div>
        );
    }, [i18n.language, t]);

    const handlePopoverVisibleChange = useCallback((eventId, visible) => {
        setPopoverVisible(prev => ({
            ...prev,
            [eventId]: visible
        }));
    }, []);

    const handleSelectEvent = useCallback((event) => {
        if (event.resource.type === 'seeMore') {
            const formattedDate = dayjs(event.start).format("YYYY-MM-DD");
            const dayData = employeeRequestCalendar?.find(d =>
                dayjs(d.date).format('YYYY-MM-DD') === formattedDate
            );

            setSelectedDate(formattedDate);
            setSelectedEmployeeList(event.resource.allEmployees || []);
            setIsDetailModalOpen(true);
        } else if (event.resource.type === 'leave' || event.resource.type === 'offsite') {
            const eventId = `${event.id}`;
            setPopoverVisible(prev => ({
                ...prev,
                [eventId]: true
            }));
        }
    }, [employeeRequestCalendar]);

    return {
        shiftColor,
        employeeRequestCalendar,
        processedEvents,
        isLeaveHrView,
        popoverVisible,
        handleHrViewChange,
        eventPropGetter,
        onChange,
        handlePopoverVisibleChange,
        getPopoverContent,
        handleSelectEvent,
        isDetailModalOpen,
        setIsDetailModalOpen,
        selectedDate,
        selectedEmployeeList,
        getCalendarRange
    }
}
export default useMyTeamLeaveCalendar;